import React, { useEffect, useMemo, useRef } from "react";
import { Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { MessageItem } from "@/components/mentorships/details/message-item";
import { ChatEntityFeatureFlagsProvider } from "@/components/shared/chat-feature-flags-context";
import type { ChatConversationMember } from "@/components/shared/chat-message-types";
import { MentorshipDetails } from "@/lib/api/mentorships";
import { MessageContextResponse } from "@/lib/api/admin";

interface ContextModalProps {
  isOpen: boolean;
  onClose: () => void;
  contextData: MessageContextResponse | undefined;
  isLoading: boolean;
  /** Pass the full mentorship OR just the mentorId — one is required */
  mentorship?: MentorshipDetails;
  mentorId?: string;
  /** Optional overrides */
  title?: string;
  subtitle?: string;
  /** Class applied to the wrapper of the target/flagged message (default: blue ring) */
  highlightClassName?: string;
  /** Extra node rendered below the target message (e.g. a flag label) */
  targetLabel?: React.ReactNode;
  /** Replace the default footer with custom content */
  footerContent?: React.ReactNode;
  /** Auto-scroll to the bottom of the message list on load */
  autoScrollToBottom?: boolean;
  /** Override DialogContent className (default: max-h-[85vh] max-w-4xl) */
  contentClassName?: string;
  /** Override the scrollable area className */
  scrollClassName?: string;
}

export function ContextModal({
  isOpen,
  onClose,
  contextData,
  isLoading,
  mentorship,
  mentorId: mentorIdProp,
  title = "Message Context",
  subtitle = "Conversation context around the selected message",
  highlightClassName = "bg-blue-50 shadow-sm ring-2 ring-blue-200",
  targetLabel,
  footerContent,
  autoScrollToBottom = false,
  contentClassName = "max-h-[85vh] max-w-4xl p-0",
  scrollClassName = "max-h-[60vh] overflow-y-auto p-4",
}: ContextModalProps) {
  const resolvedMentorId = mentorIdProp ?? mentorship?.mentor?.id;
  const conversationMembers: ChatConversationMember[] | undefined = useMemo(
    () =>
      mentorship
        ? [
            {
              id: mentorship.mentor.id,
              name: mentorship.mentor.name,
              avatarUrl: mentorship.mentor.profile_picture_url,
              systemRoles: mentorship.mentor.systemRoles,
            },
            {
              id: mentorship.mentee.id,
              name: mentorship.mentee.name,
              avatarUrl: mentorship.mentee.profile_picture_url,
              systemRoles: mentorship.mentee.systemRoles,
            },
          ]
        : undefined,
    [mentorship],
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScrollToBottom && !isLoading && contextData) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      });
    }
  }, [autoScrollToBottom, isLoading, contextData]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={contentClassName}>
        <DialogHeader className="border-b p-4">
          <DialogTitle className="flex items-center gap-2 text-xl font-medium text-[#1f1e1d]">
            {title}
          </DialogTitle>
          <p className="mt-1 text-sm text-gray-600">{subtitle}</p>
        </DialogHeader>

        <div ref={scrollRef} className={scrollClassName}>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="mb-3 h-8 w-8 animate-spin text-blue-600" />
              <p className="text-gray-500">Loading context...</p>
            </div>
          ) : contextData ? (
            <ChatEntityFeatureFlagsProvider>
              <div>
                {contextData.messages.map((message) => {
                  const isTarget = message.isTarget;
                  return (
                    <div
                      key={message.message_id}
                      className={`transition-all duration-200 ${
                        isTarget
                          ? `mb-3 px-3 py-3 ${highlightClassName}`
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <MessageItem
                        message={message}
                        showOnRightSide={
                          message.user?.user_id === resolvedMentorId
                        }
                        isHighlighted={isTarget}
                        conversationMembers={conversationMembers}
                      />
                      {isTarget && targetLabel}
                    </div>
                  );
                })}
              </div>
            </ChatEntityFeatureFlagsProvider>
          ) : (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 p-4">
                <MessageSquare className="h-8 w-8 text-red-400" />
              </div>
              <p className="text-lg font-medium text-gray-500">
                Failed to load context
              </p>
              <p className="mt-1 text-sm text-gray-400">Please try again</p>
            </div>
          )}
        </div>

        <DialogFooter className="border-t p-4">
          {footerContent ?? (
            <Button variant="outline" onClick={onClose} className="px-6">
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
